var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// aspect-schemas/DateTimeAspect.ts
import { Int } from "@adornis/baseql/baseqlTypes.js";
import { Entity, Field } from "@adornis/baseql/decorators.js";
import { registerEntity } from "@adornis/baseql/metadata/register.js";
import { html } from "lit";
import { DateTime } from "luxon";
import { getMongoRangeFilter, renderRangePreview } from "../range-helper.js";
import { AbstractAspect } from "./AbstractAspect.js";
var units = ["years", "months", "weeks", "days", "hours", "minutes", "seconds"];
var modeDisplay = {
  is: "is",
  range: "range",
  before: "before",
  after: "after",
  beforeToday: "beforeToday",
  afterToday: "afterToday"
};
var DateTimeAspect = class extends AbstractAspect {
  constructor(doc) {
    super(doc);
  }
  getMongoFilter() {
    const { firstValue, secondValue, diffAmount, diffUnit, fieldName, mode } = this;
    if (!fieldName)
      return null;
    if (mode === "is" || mode === "range")
      return getMongoRangeFilter({
        min: firstValue,
        max: secondValue,
        fieldName,
        convertToComparableValue: (value) => value.toJSDate()
      });
    if (mode === "before")
      return getMongoRangeFilter({
        max: secondValue,
        fieldName,
        convertToComparableValue: (value) => value.toJSDate()
      });
    if (mode === "after")
      return getMongoRangeFilter({
        min: firstValue,
        fieldName,
        convertToComparableValue: (value) => value.toJSDate()
      });
    if (mode === "beforeToday") {
      if (!diffAmount || !diffUnit)
        return null;
      const now = DateTime.now();
      return getMongoRangeFilter({
        min: now.minus({ [diffUnit]: diffAmount }),
        max: now,
        fieldName,
        convertToComparableValue: (value) => value.toJSDate()
      });
    }
    if (mode === "afterToday") {
      if (!diffAmount || !diffUnit)
        return null;
      const now = DateTime.now();
      return getMongoRangeFilter({
        min: now,
        max: now.plus({ [diffUnit]: diffAmount }),
        fieldName,
        convertToComparableValue: (value) => value.toJSDate()
      });
    }
    return null;
  }
};
DateTimeAspect._class = "DateTimeAspect";
__decorateClass([
  Field((type) => DateTime)
], DateTimeAspect.prototype, "firstValue", 2);
__decorateClass([
  Field((type) => DateTime)
], DateTimeAspect.prototype, "secondValue", 2);
__decorateClass([
  Field((type) => String, { default: (v) => v ?? "is" })
], DateTimeAspect.prototype, "mode", 2);
__decorateClass([
  Field((type) => Int)
], DateTimeAspect.prototype, "diffAmount", 2);
__decorateClass([
  Field((type) => String)
], DateTimeAspect.prototype, "diffUnit", 2);
DateTimeAspect = __decorateClass([
  Entity()
], DateTimeAspect);
var DateTimeAspectSchema = (mode) => {
  class DateTimeVariationAspect extends DateTimeAspect {
    constructor(doc) {
      doc.mode = mode;
      super(doc);
    }
  }
  DateTimeVariationAspect._class = `DateTime${mode.toUpperCase()}Aspect`;
  registerEntity(DateTimeVariationAspect);
  let config = {
    aspectConstructor: DateTimeVariationAspect,
    renderEditor: () => html`mode "${mode}" is not implemented`,
    renderPreview: () => html`mode "${mode}" is not implemented`
  };
  switch (mode) {
    case "range":
      config.renderEditor = ({ aspectValue }) => html`
        <x-flex horizontal space="sm" crossaxis-center>
          <x-text tone="subtle"> Minimum </x-text>
          <x-input
            clearable
            type="date"
            .value=${aspectValue.firstValue}
            placeholder="Datum"
            placeholder-mode="hide"
            @value-picked=${(e) => {
        if (!e.detail.value) {
          aspectValue.firstValue = null;
          return;
        }
        aspectValue.firstValue = e.detail.value.startOf("day");
      }}
          ></x-input>
          <x-text tone="subtle"> Maximum </x-text>
          <x-input
            clearable
            type="date"
            .value=${aspectValue.secondValue}
            placeholder="Datum"
            placeholder-mode="hide"
            @value-picked=${(e) => {
        if (!e.detail.value) {
          aspectValue.secondValue = null;
          return;
        }
        aspectValue.secondValue = e.detail.value.endOf("day");
      }}
          ></x-input>
        </x-flex>
      `;
      config.renderPreview = ({ aspectValue: { firstValue, secondValue } }) => renderRangePreview({
        min: firstValue,
        max: secondValue,
        convertToDisplayValue: (value) => value.toLocaleString(DateTime.DATE_SHORT),
        greaterString: "Nach",
        lesserString: "Vor"
      });
      break;
    case "is":
      config.renderEditor = ({ aspectValue }) => html`
        <x-input
          clearable
          type="date"
          .value=${aspectValue.firstValue}
          placeholder="Datum"
          placeholder-mode="hide"
          @value-picked=${(e) => {
        if (!e.detail.value) {
          aspectValue.secondValue = null;
          aspectValue.firstValue = null;
          return;
        }
        aspectValue.firstValue = e.detail.value.startOf("day");
        aspectValue.secondValue = e.detail.value.endOf("day");
      }}
        ></x-input>
      `;
      config.renderPreview = ({ aspectValue: { firstValue } }) => firstValue ? html` ${firstValue.toLocaleString()}` : null;
      break;
    case "before":
      config.renderEditor = ({ aspectValue }) => html`
        <x-input
          clearable
          type="date"
          .value=${aspectValue.secondValue}
          placeholder="Datum"
          placeholder-mode="hide"
          @value-picked=${(e) => {
        if (!e.detail.value) {
          aspectValue.secondValue = null;
          aspectValue.firstValue = null;
          return;
        }
        aspectValue.secondValue = e.detail.value.startOf("day");
        aspectValue.firstValue = e.detail.value.endOf("day");
      }}
        ></x-input>
      `;
      config.renderPreview = ({ aspectValue: { secondValue } }) => secondValue ? html` ${secondValue.toLocaleString()}` : null;
      break;
    case "after":
      config.renderEditor = ({ aspectValue }) => html`
        <x-input
          clearable
          type="date"
          .value=${aspectValue.firstValue}
          placeholder="Datum"
          placeholder-mode="hide"
          @value-picked=${(e) => {
        if (!e.detail.value) {
          aspectValue.secondValue = null;
          aspectValue.firstValue = null;
          return;
        }
        aspectValue.firstValue = e.detail.value.endOf("day");
        aspectValue.secondValue = e.detail.value.startOf("day");
      }}
        ></x-input>
      `;
      config.renderPreview = ({ aspectValue: { firstValue } }) => firstValue ? html` ${firstValue.toLocaleString()}` : null;
      break;
    case "beforeToday":
      config.renderEditor = ({ aspectValue, translation }) => {
        return html`
          <x-flex horizontal space="sm" crossaxis-center>
            <x-input
              clearable
              type="number"
              .value=${aspectValue.diffAmount}
              placeholder="Anzahl"
              placeholder-mode="hide"
              @value-picked=${(e) => {
          if (!e.detail.value) {
            aspectValue.diffAmount = null;
            return;
          }
          aspectValue.diffAmount = e.detail.value;
        }}
            ></x-input>
            <x-dropdown-selection
              flex
              style="min-width: 14ch"
              placeholder="Einheit"
              .selectables=${units}
              .renderString=${(s) => s ? translation.translate("units", s) : ""}
              .value=${aspectValue.diffUnit}
              @value-picked=${(e) => aspectValue.diffUnit = e.detail.value}
            >
            </x-dropdown-selection>
          </x-flex>
        `;
      };
      config.renderPreview = ({ aspectValue: { diffAmount, diffUnit }, translation }) => diffAmount && diffUnit ? html` ${diffAmount} ${translation.translate("units", diffUnit)}` : null;
      break;
    case "afterToday":
      config.renderEditor = ({ aspectValue, translation }) => {
        return html`
          <x-flex horizontal space="sm" crossaxis-center>
            <x-input
              clearable
              type="number"
              .value=${aspectValue.diffAmount}
              placeholder="Anzahl"
              placeholder-mode="hide"
              @value-picked=${(e) => {
          if (!e.detail.value) {
            aspectValue.diffAmount = null;
            return;
          }
          aspectValue.diffAmount = e.detail.value;
        }}
            ></x-input>
            <x-dropdown-selection
              flex
              style="min-width: 14ch"
              placeholder="Einheit"
              .selectables=${units}
              .renderString=${(s) => s ? translation.translate("units", s) : ""}
              .value=${aspectValue.diffUnit}
              @value-picked=${(e) => aspectValue.diffUnit = e.detail.value}
            >
            </x-dropdown-selection>
          </x-flex>
        `;
      };
      config.renderPreview = ({ aspectValue: { diffAmount, diffUnit }, translation }) => diffAmount && diffUnit ? html` ${diffAmount} ${translation.translate("units", diffUnit)}` : null;
      break;
    default:
      break;
  }
  return config;
};
var DateTimeVariations = Object.keys(modeDisplay).map((mode) => {
  return DateTimeAspectSchema(mode);
});
export {
  DateTimeAspect,
  DateTimeAspectSchema,
  DateTimeVariations
};
